import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Upload, CheckCircle, X, Key } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to format payment reference
const formatPaymentReference = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = date.getDate();
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEPT', 'OCT', 'NOV', 'DEC'];
  const month = months[date.getMonth()];
  return `${day}${month} COMP`;
};

export default function EnterCompetition({ user, club, onLogout }) {
  const { competitionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [competition, setCompetition] = useState(null);
  const [entry, setEntry] = useState(null);
  const [gymnasts, setGymnasts] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [judges, setJudges] = useState([]);
  const [selectedGymnasts, setSelectedGymnasts] = useState([]);
  const [gymnastCategories, setGymnastCategories] = useState({});  // {gymnastId: category}
  const [categories, setCategories] = useState([]);
  const [selectedCoaches, setSelectedCoaches] = useState([]);
  const [selectedJudges, setSelectedJudges] = useState([]);
  const [judgeDayAssignments, setJudgeDayAssignments] = useState({}); // {judgeId: [1, 2]}
  const [coachSmsToggles, setCoachSmsToggles] = useState({}); // {coachId: bool}
  const [petitionedGymnasts, setPetitionedGymnasts] = useState([]);
  const [paymentFile, setPaymentFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [coachConfirmation, setCoachConfirmation] = useState(false);
  const [judgeConfirmation, setJudgeConfirmation] = useState(false);
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [showWithdrawBlockedDialog, setShowWithdrawBlockedDialog] = useState(false);
  const [gymnastToWithdraw, setGymnastToWithdraw] = useState(null);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [withdrawNotes, setWithdrawNotes] = useState('');
  const [refundConfirmation, setRefundConfirmation] = useState(false);
  const [refundCaseId, setRefundCaseId] = useState(null);
  
  // Judge Bypass state
  const [showBypassDialog, setShowBypassDialog] = useState(false);
  const [bypassCode, setBypassCode] = useState('');
  const [bypassUsed, setBypassUsed] = useState(false);
  const [bypassLoading, setBypassLoading] = useState(false);
  const [judgeFineAcknowledged, setJudgeFineAcknowledged] = useState(false); // accepts fine when short on judges

  // Team Events state
  const [teams, setTeams] = useState([]);
  const [teamName, setTeamName] = useState('');
  const [teamGymnastIds, setTeamGymnastIds] = useState([]);
  const [teamDiscipline, setTeamDiscipline] = useState('');
  const [teamCategory, setTeamCategory] = useState('');
  const [teamCategoryName, setTeamCategoryName] = useState('');  // named team category (Junior/Senior) when comp defines them
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [teamSaving, setTeamSaving] = useState(false);
  const [showTeamPrompt, setShowTeamPrompt] = useState(false); // pre-submit "add a team?" dialog
  const [teamCategoryOptions, setTeamCategoryOptions] = useState([]); // team-type categories from categoriesConfig

  useEffect(() => {
    loadData();
  }, [competitionId]);

  const loadData = async () => {
    try {
      // Step 1: load competition list first — this must succeed or we show "not found"
      const compRes = await axios.get(`${API}/competitions`);
      const comp = compRes.data.find(c => c.id === competitionId);
      if (!comp) {
        setLoading(false);
        return;
      }
      // Set competition immediately so the page renders even if later requests fail
      setCompetition(comp);

      const allCatCfg = comp?.categoriesConfig || [];
      const individualCatNames = allCatCfg
        .filter(c => c.categoryType !== 'team')
        .map(c => c.name);
      const teamCatCfg = allCatCfg.filter(c => c.categoryType === 'team');
      setCategories(individualCatNames.length > 0 ? individualCatNames : (comp?.categories || []));
      setTeamCategoryOptions(teamCatCfg);

      // Step 2: load entry, gymnasts, coaches, judges, teams — graceful degradation on any failure
      const emptyEntry = { gymnastIds: [], coachIds: [], judgeIds: [], petitionedGymnasts: [], withdrawnGymnasts: [], judgeBypassUsed: false, judgeFineAcknowledged: false, judgeDayAssignments: {}, total: 0 };
      const [entryRes, gymRes, coachRes, judgeRes, teamsRes] = await Promise.all([
        axios.get(`${API}/entries/${competitionId}`).catch(err => {
          const status = err?.response?.status;
          const detail = err?.response?.data?.detail;
          if (status === 403) {
            toast.error(detail || 'Your club account needs attention — please log out and back in, or contact the organiser.', { duration: 10000 });
          } else if (status === 401) {
            toast.error('Session expired — please log out and log back in.', { duration: 8000 });
          } else {
            console.error('Failed to load entry data:', err);
          }
          return { data: emptyEntry };
        }),
        axios.get(`${API}/gymnasts`).catch(() => ({ data: [] })),
        axios.get(`${API}/coaches`).catch(() => ({ data: [] })),
        axios.get(`${API}/judges`).catch(() => ({ data: [] })),
        axios.get(`${API}/teams/${competitionId}`).catch(() => ({ data: { teams: [] } }))
      ]);

      setEntry(entryRes.data);
      setGymnasts(gymRes.data);
      setCoaches(coachRes.data);
      setJudges(judgeRes.data);

      // Pre-select from existing entry - but only include items that still exist
      const existingGymnastIds = new Set(gymRes.data.map(g => g.id));
      const existingCoachIds = new Set(coachRes.data.map(c => c.id));
      const existingJudgeIds = new Set(judgeRes.data.map(j => j.id));
      
      // Filter out any IDs that no longer exist in the club's records
      const validGymnastIds = (entryRes.data.gymnastIds || []).filter(id => existingGymnastIds.has(id));
      const validCoachIds = (entryRes.data.coachIds || []).filter(id => existingCoachIds.has(id));
      const validJudgeIds = (entryRes.data.judgeIds || []).filter(id => existingJudgeIds.has(id));
      
      setSelectedGymnasts(validGymnastIds);
      setGymnastCategories(entryRes.data.gymnastCategories || {});
      setSelectedCoaches(validCoachIds);
      setSelectedJudges(validJudgeIds);
      setCoachSmsToggles(entryRes.data.coachSmsToggles || {});
      setPetitionedGymnasts(entryRes.data.petitionedGymnasts || []);
      setBypassUsed(entryRes.data.judgeBypassUsed || false);
      setJudgeDayAssignments(entryRes.data.judgeDayAssignments || {});
      setJudgeFineAcknowledged(entryRes.data.judgeFineAcknowledged || false);
      // Restore confirmation checkboxes — if entry is submitted these must have been ticked
      if (entryRes.data.submittedAt) {
        if (validJudgeIds.length > 0) setJudgeConfirmation(true);
        if (validCoachIds.length > 0) setCoachConfirmation(true);
      }
      setTeams(teamsRes.data?.teams || []);
    } catch (error) {
      console.error('Failed to load competition list:', error);
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 403) {
        toast.error(detail || 'Your club account needs attention — please log out and back in, or contact the organiser.', { duration: 10000 });
      } else if (status === 401) {
        toast.error('Session expired — please log out and log back in.', { duration: 8000 });
      } else {
        toast.error('Failed to load competition data', { duration: 6000 });
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-suggest a category for a gymnast based on their DOB.
  // Gymnastics eligibility rule: age is the age the gymnast TURNS in the competition year —
  // so a gymnast born any time in 2018 is "age 8" in a 2026 competition regardless of birthday month.
  const suggestCategoryForGymnast = (gymnast) => {
    if (!gymnast?.dob) return null;
    const birthYear = new Date(gymnast.dob).getFullYear();
    const compYear = competition?.startsOn
      ? new Date(competition.startsOn).getFullYear()
      : new Date().getFullYear();

    // Helper: is this birthYear eligible for a given age band?
    const eligibleForBand = (minAge, maxAge) => {
      // minAge → gymnast must turn at LEAST minAge this year → born compYear-minAge or earlier
      if (minAge != null && birthYear > compYear - minAge) return false;
      // maxAge → gymnast must turn at MOST maxAge this year → born compYear-maxAge or later
      if (maxAge != null && birthYear < compYear - maxAge) return false;
      return true;
    };

    // 1. Check categoryAgeBands (simple per-category age bands set in comp settings)
    const ageBands = competition?.categoryAgeBands || {};
    const bandMatches = categories.filter(cat => {
      const band = ageBands[cat];
      if (!band) return false;
      return eligibleForBand(band.minAge, band.maxAge);
    });
    if (bandMatches.length) {
      // Prefer tightest range (both min and max set)
      const full = bandMatches.filter(cat => ageBands[cat]?.minAge && ageBands[cat]?.maxAge);
      return full.length ? full[0] : bandMatches[0];
    }

    // 2. Fall back to categoriesConfig (advanced config with birth years)
    const cfg = competition?.categoriesConfig;
    if (!cfg || !Array.isArray(cfg)) return null;

    const validCats = cfg.filter(c => {
      if (!categories.includes(c.name)) return false;
      const minY = c.minBirthYear ? parseInt(c.minBirthYear) : null;
      const maxY = c.maxBirthYear ? parseInt(c.maxBirthYear) : null;
      // Also handle minAge/maxAge on categoriesConfig entries
      const effMinY = minY ?? (c.maxAge != null ? compYear - c.maxAge : null);
      const effMaxY = maxY ?? (c.minAge != null ? compYear - c.minAge : null);
      if (effMinY !== null && effMaxY !== null) return birthYear >= effMinY && birthYear <= effMaxY;
      if (effMinY !== null) return birthYear >= effMinY;
      if (effMaxY !== null) return birthYear <= effMaxY;
      return false;
    });

    if (!validCats.length) return null;
    const specific = validCats.filter(c => c.minBirthYear && c.maxBirthYear);
    if (specific.length) return specific[0].name;
    const maxOnly = validCats.filter(c => c.maxBirthYear && !c.minBirthYear);
    if (maxOnly.length) {
      maxOnly.sort((a, b) => parseInt(a.maxBirthYear) - parseInt(b.maxBirthYear));
      return maxOnly[0].name;
    }
    return validCats[0].name;
  };

  const handleGymnastToggle = (id) => {
    if (selectedGymnasts.includes(id)) {
      setSelectedGymnasts(prev => prev.filter(gid => gid !== id));
      // Clear their category on uncheck so DOB auto-suggest fires fresh on re-check
      setGymnastCategories(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } else {
      setSelectedGymnasts(prev => [...prev, id]);
      // Auto-suggest category from DOB if not already set
      if (!gymnastCategories[id]) {
        const gymnast = gymnasts.find(g => g.id === id);
        const suggested = suggestCategoryForGymnast(gymnast);
        if (suggested) {
          setGymnastCategories(prev => ({ ...prev, [id]: suggested }));
        }
      }
    }
  };

  const handleCoachToggle = (id) => {
    setSelectedCoaches(prev =>
      prev.includes(id) ? prev.filter(cid => cid !== id) : [...prev, id]
    );
  };

  const handleJudgeToggle = (id) => {
    if (selectedJudges.includes(id)) {
      setSelectedJudges(prev => prev.filter(jid => jid !== id));
      setJudgeDayAssignments(prev => { const n = { ...prev }; delete n[id]; return n; });
    } else {
      setSelectedJudges(prev => [...prev, id]);
      // Default: all competition days checked so 1 judge can cover all required judge-days
      const allDays = Array.from({ length: competition?.competitionDays || 1 }, (_, i) => i + 1);
      setJudgeDayAssignments(prev => ({ ...prev, [id]: allDays }));
    }
  };

  const handlePetitionedGymnastToggle = (id) => {
    setPetitionedGymnasts(prev => {
      const existing = prev.find(p => p.gymnastId === id);
      if (existing) {
        // Remove from petitioned list
        return prev.filter(p => p.gymnastId !== id);
      } else {
        // Add to petitioned list with pending status
        const gymnast = gymnasts.find(g => g.id === id);
        if (!gymnast) return prev;
        
        return [...prev, {
          gymnastId: id,
          gymnastName: `${gymnast.firstName} ${gymnast.lastName}`,
          status: 'pending'
        }];
      }
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setPaymentFile(file);
    }
  };

  const handleUploadPayment = async () => {
    if (!paymentFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', paymentFile);

      const response = await axios.post(
        `${API}/entries/${competitionId}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setEntry({ ...entry, paymentProofUrl: response.data.paymentProofUrl });
      toast.success('Payment proof uploaded successfully');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload payment proof');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/entries/${competitionId}`, {
        gymnastIds: selectedGymnasts,
        gymnastCategories: gymnastCategories,
        coachIds: selectedCoaches,
        judgeIds: selectedJudges,
        petitionedGymnasts: petitionedGymnasts,
        coachSmsToggles: coachSmsToggles,
        judgeFineAcknowledged: judgeFineAcknowledged,
        judgeDayAssignments: judgeDayAssignments
      });
      toast.success('Entry saved successfully');
      loadData(); // Reload to get updated total
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('Failed to save entry');
    }
  };

  const handleSubmit = async () => {
    // Per-competition entry requirements (default true to preserve existing behaviour)
    const gymnastsRequired = competition?.gymnastsRequired !== false;
    const coachesRequired = competition?.coachesRequired !== false;
    const judgesRequired = competition?.judgesRequired !== false;
    const minCoaches = Number.isFinite(competition?.minCoaches) ? competition.minCoaches : 1;
    const minJudges = Number.isFinite(competition?.minJudges) ? competition.minJudges : 1;

    if (gymnastsRequired && selectedGymnasts.length === 0) {
      toast.error('Please select at least one gymnast');
      return;
    }

    // Check all selected gymnasts have categories
    const missingCategories = selectedGymnasts.filter(id => !gymnastCategories[id]);
    if (missingCategories.length > 0) {
      toast.error('Please select a category for all gymnasts');
      return;
    }

    // Check all petitioned gymnasts (pending status) have categories
    const pendingPetitions = petitionedGymnasts.filter(p => p.status === 'pending');
    const missingPetitionCategories = pendingPetitions.filter(p => !gymnastCategories[p.gymnastId]);
    if (missingPetitionCategories.length > 0) {
      toast.error('Please select a category for all petitioned gymnasts');
      return;
    }

    if (coachesRequired && selectedCoaches.length < minCoaches) {
      toast.error(`Please select at least ${minCoaches} coach${minCoaches !== 1 ? 'es' : ''}`);
      return;
    }

    // SMS: at least 1 coach must have SMS enabled when the feature is on
    if (competition?.smsCoachesEnabled && selectedCoaches.length > 0) {
      const smsCount = selectedCoaches.filter(id => coachSmsToggles[id]).length;
      if (smsCount === 0) {
        toast.error('Please enable SMS alerts for at least 1 coach');
        return;
      }
    }

    // Judge requirement - ladder-aware; bypassed by code OR by fine acknowledgement
    const minJudgesNeeded = requiredJudgesForCurrentEntry();
    if (judgesRequired && totalJudgeDays() < minJudgesNeeded && !bypassUsed && !judgeFineAcknowledged) {
      toast.error(`Please provide at least ${minJudgesNeeded} judge-day${minJudgesNeeded !== 1 ? 's' : ''}, or acknowledge the fine`);
      return;
    }

    // Only require judge confirmation if judges are selected
    if (selectedJudges.length > 0 && !judgeConfirmation) {
      toast.error('Please confirm that your judges are aware of their selection');
      return;
    }

    if (selectedCoaches.length > 0 && !coachConfirmation) {
      toast.error('Please confirm that your coaches have the required BG membership');
      return;
    }

    if (competition?.paymentProofRequired !== false && !entry?.paymentProofUrl) {
      toast.error('Please upload payment screenshot before submitting');
      return;
    }

    setSubmitting(true);
    try {
      // First save selections (including petitioned gymnasts and categories)
      await axios.put(`${API}/entries/${competitionId}`, {
        gymnastIds: selectedGymnasts,
        gymnastCategories: gymnastCategories,
        coachIds: selectedCoaches,
        judgeIds: selectedJudges,
        petitionedGymnasts: petitionedGymnasts,
        coachSmsToggles: coachSmsToggles,
        judgeFineAcknowledged: judgeFineAcknowledged,
        judgeDayAssignments: judgeDayAssignments
      });
      await axios.post(`${API}/entries/${competitionId}/submit`);
      toast.success('Entry submitted successfully!');
      setTimeout(() => loadData(), 1500);
    } catch (error) {
      console.error('Submit failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePreSubmit = () => {
    // If team events are available and no teams entered yet, prompt the user first
    const hasTeamEvent = competition?.teamEventEnabled;
    const hasTeamCategories = teamCategoryOptions.length > 0 || (competition?.teamCategories?.length > 0);
    const activeTeams = teams.filter(t => t.status !== 'withdrawn');
    if (hasTeamEvent && hasTeamCategories && activeTeams.length === 0) {
      setShowTeamPrompt(true);
    } else {
      handleSubmit();
    }
  };

  const handleOpenWithdrawDialog = (gymnast) => {
    setGymnastToWithdraw(gymnast);
    setWithdrawReason('');
    setWithdrawNotes('');
    setRefundConfirmation(false);
    setRefundCaseId(null);
    setShowWithdrawDialog(true);
  };

  const handleWithdrawGymnast = async (startRefund = false) => {
    if (!withdrawReason) {
      toast.error('Please select a withdrawal reason');
      return;
    }

    // If "Other" is selected, notes are required
    if (withdrawReason === 'Other' && !withdrawNotes.trim()) {
      toast.error('Please specify the reason for withdrawal');
      return;
    }

    if (startRefund && !refundConfirmation) {
      toast.error('Please confirm you understand the refund requirements');
      return;
    }

    try {
      const response = await axios.post(`${API}/entries/${competitionId}/withdraw-gymnast`, {
        gymnastId: gymnastToWithdraw.id,
        reason: withdrawReason,
        notes: withdrawNotes,
        startRefundClaim: startRefund
      });

      const firstName = gymnastToWithdraw.preferredFirstName || gymnastToWithdraw.firstName;
      const lastName = gymnastToWithdraw.preferredLastName || gymnastToWithdraw.lastName;
      
      if (startRefund && response.data.refundCaseId) {
        setRefundCaseId(response.data.refundCaseId);
        toast.success(
          `Withdrawal completed. Refund request #${response.data.refundCaseId} for ${firstName} created. Email evidence to support@scorz.co.uk within 7 days.`,
          { duration: 8000 }
        );
      } else {
        toast.success(`${firstName} ${lastName} withdrawn successfully`);
        setShowWithdrawDialog(false);
      }
      
      loadData(); // Reload entry data
    } catch (error) {
      console.error('Withdrawal failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to withdraw gymnast');
    }
  };

  // Judge Bypass Handler
  const handleBypassSubmit = async () => {
    if (!bypassCode.trim()) {
      toast.error('Please enter the bypass code');
      return;
    }

    setBypassLoading(true);
    try {
      await axios.post(`${API}/competitions/${competitionId}/validate-judge-bypass`, {
        code: bypassCode.trim().toUpperCase()
      });
      
      setBypassUsed(true);
      setShowBypassDialog(false);
      setBypassCode('');
      toast.success('Bypass code accepted. You may proceed without a judge.');
    } catch (error) {
      console.error('Bypass validation failed:', error);
      toast.error(error.response?.data?.detail || 'Invalid bypass code');
    } finally {
      setBypassLoading(false);
    }
  };

  const feeForCategory = (category) => {
    if (!competition) return 0;
    const cf = competition.categoryFees || {};
    if (category && cf[category] !== undefined && cf[category] !== null) {
      const n = parseFloat(cf[category]);
      return Number.isFinite(n) ? n : 0;
    }
    const def = parseFloat(competition.entryFee);
    return Number.isFinite(def) ? def : 0;
  };

  // Total judge-day slots covered (1 judge × 2 days = 2 slots)
  const totalJudgeDays = () => {
    const days = competition?.competitionDays || 1;
    if (days <= 1) return selectedJudges.length;
    return selectedJudges.reduce((sum, id) => sum + (judgeDayAssignments[id]?.length || 1), 0);
  };

  // Human-readable day labels ("Sat 12 Jul" or "Day 1")
  const getCompDayLabels = () => {
    const days = competition?.competitionDays || 1;
    const start = competition?.startsOn ? new Date(competition.startsOn) : null;
    return Array.from({ length: days }, (_, i) => {
      if (start) {
        const d = new Date(start);
        d.setDate(d.getDate() + i);
        return format(d, 'EEE d MMM');
      }
      return `Day ${i + 1}`;
    });
  };

  const handleJudgeDayToggle = (judgeId, dayNum, checked) => {
    setJudgeDayAssignments(prev => {
      const current = prev[judgeId] || [1];
      const updated = checked
        ? [...new Set([...current, dayNum])].sort()
        : current.filter(d => d !== dayNum);
      return { ...prev, [judgeId]: updated.length > 0 ? updated : [dayNum] };
    });
  };

  // Resolve the minimum judges required for the current gymnast count using
  // the per-competition ladder if set, otherwise the flat minJudges floor.
  const requiredJudgesForCurrentEntry = () => {
    if (!competition) return 0;
    const ladder = competition.judgeRequirementLadder;
    const gymnastCount = selectedGymnasts.length;
    if (Array.isArray(ladder) && ladder.length > 0) {
      if (gymnastCount === 0) return 0;
      const eligible = ladder.filter(r => Number(r?.minGymnasts ?? 0) <= gymnastCount);
      if (eligible.length === 0) return 0;
      const winner = eligible.reduce((a, b) => (Number(a.minGymnasts ?? 0) >= Number(b.minGymnasts ?? 0) ? a : b));
      return Math.max(0, Number(winner.judgesRequired ?? 0));
    }
    return Number.isFinite(competition?.minJudges) ? competition.minJudges : 1;
  };

  const calculateTotal = () => {
    if (!competition) return 0;
    const gymnastsTotal = selectedGymnasts.reduce((sum, gid) => {
      return sum + feeForCategory(gymnastCategories[gid]);
    }, 0);
    // Active teams contribute the locked-in feePence (already stored on each team)
    const teamsTotal = (teams || [])
      .filter(t => t.status !== 'withdrawn')
      .reduce((sum, t) => sum + ((t.feePence || 0) / 100), 0);
    return gymnastsTotal + teamsTotal;
  };

  // ===== Team Events handlers =====
  const resetTeamForm = () => {
    setTeamName('');
    setTeamGymnastIds([]);
    setTeamDiscipline('');
    setTeamCategory('');
    setTeamCategoryName('');
    setEditingTeamId(null);
    setShowTeamForm(false);
  };

  const openCreateTeamForm = () => {
    resetTeamForm();
    // Default team name to club name — will append category when selected
    setTeamName(club?.name || '');
    setShowTeamForm(true);
  };

  const openEditTeamForm = (team) => {
    setEditingTeamId(team.id);
    setTeamName(team.name || '');
    setTeamGymnastIds([...(team.gymnastIds || [])]);
    setTeamDiscipline(team.discipline || '');
    setTeamCategory(team.category || '');
    setTeamCategoryName(team.teamCategoryName || '');
    setShowTeamForm(true);
  };

  const toggleTeamGymnast = (gid) => {
    const teamSize = Number.isFinite(competition?.teamSize) ? competition.teamSize : 6;
    setTeamGymnastIds(prev => {
      if (prev.includes(gid)) return prev.filter(x => x !== gid);
      if (prev.length >= teamSize) {
        toast.error(`A team can have at most ${teamSize} gymnasts`);
        return prev;
      }
      return [...prev, gid];
    });
  };

  const handleTeamCategoryChange = (catName) => {
    const val = catName === 'none' ? '' : catName;
    setTeamCategoryName(val);
    // Auto-update team name if it's still the default (club name only or "Club - OldCat")
    if (val && club?.name) {
      const currentName = teamName.trim();
      const clubName = club.name.trim();
      const isDefaultName = currentName === clubName || currentName === '' ||
        currentName.startsWith(clubName + ' - ');
      if (isDefaultName) {
        setTeamName(`${clubName} - ${val}`);
      }
    }
  };


  const handleSaveTeam = async () => {
    if (!teamName.trim()) {
      toast.error('Please enter a team name');
      return;
    }
    if (teamGymnastIds.length === 0) {
      toast.error('A team needs at least one gymnast');
      return;
    }
    setTeamSaving(true);
    try {
      const payload = {
        name: teamName.trim(),
        gymnastIds: teamGymnastIds,
        discipline: teamDiscipline || null,
        category: teamCategory || null,
        teamCategoryName: teamCategoryName || null,
      };
      if (editingTeamId) {
        await axios.put(`${API}/teams/${editingTeamId}`, payload);
        toast.success('Team updated');
      } else {
        await axios.post(`${API}/teams/${competitionId}`, payload);
        toast.success('Team created');
      }
      resetTeamForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save team');
    } finally {
      setTeamSaving(false);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Delete this team? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/teams/${teamId}`);
      toast.success('Team deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete team');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-black text-xl">Loading...</div>
      </div>
    );
  }

  if (!loading && !competition) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white gap-4 px-6 text-center">
        <div className="text-black text-xl font-bold">Competition not found</div>
        <div className="text-gray-500 text-sm max-w-sm">
          This may be because your club account isn't fully set up, or your session has expired.<br />
          Try logging out and logging back in. If the problem persists, contact the organiser.
        </div>
        <button
          onClick={() => { localStorage.removeItem('token'); window.location.href = '/'; }}
          className="mt-2 px-6 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800"
        >
          Log out &amp; try again
        </button>
      </div>
    );
  }

  const isSubmitted = !!entry?.submittedAt;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
              <Button
                data-testid="back-to-dashboard-button"
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                size="sm"
                className="text-[#000000] p-2"
              >
                <ArrowLeft className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <img src="/scorz-logo.png" alt="Scorz Logo" className="h-10 sm:h-12 w-auto" />
              <div className="flex-1">
                <h1 className="text-base sm:text-2xl font-bold text-black break-words" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                  {competition.title}
                </h1>
                <p className="text-gray-600 text-xs sm:text-sm">
                  {competition.displayDate || `${format(new Date(competition.startsOn), 'dd/MM/yyyy')} - ${format(new Date(competition.endsOn), 'dd/MM/yyyy')}`}
                </p>
              </div>
            </div>
            {isSubmitted && (
              <div data-testid="entry-submitted-badge" className="flex items-center gap-2 text-green-600 font-semibold text-sm whitespace-nowrap">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                Submitted
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {competition?.entryEligibilityText && (
          <div className="mb-6 p-4 rounded-lg border-l-4 border-l-black bg-gray-50 text-sm text-gray-800" data-testid="entry-eligibility-banner">
            <span className="font-semibold">Eligibility:</span> {competition.entryEligibilityText}
          </div>
        )}
        {competition?.photographyOptOutEmail && (
          <div className="mb-6 p-3 rounded-lg border bg-white text-xs text-gray-600" data-testid="entry-photo-optout-banner">
            <span className="font-semibold">Photography opt-out:</span> email <a className="underline" href={`mailto:${competition.photographyOptOutEmail}`}>{competition.photographyOptOutEmail}</a> before the event if you do not want your gymnast photographed or videoed.
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Selection Cards */}
          <div className="lg:col-span-2 space-y-6">
            {/* Gymnasts */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg sm:text-xl">
                  Select Gymnasts
                  {(() => {
                    const cf = competition.categoryFees || {};
                    const distinct = new Set(Object.values(cf).map(v => parseFloat(v)).filter(v => Number.isFinite(v)));
                    if (Object.keys(cf).length === 0 || (distinct.size === 1 && parseFloat(competition.entryFee) === [...distinct][0])) {
                      return ` (£${parseFloat(competition.entryFee || 0).toFixed(2)} each)`;
                    }
                    return ' (per-category pricing)';
                  })()}
                </CardTitle>
                <CardDescription className="text-xs leading-relaxed">
                  <strong>Please Note:</strong> It is the responsibility of the CLUB to ensure that they have entered their gymnasts into the correct level for their current ability and have the relevant BG membership.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {gymnasts.length === 0 ? (
                  <p className="text-gray-500">No gymnasts added yet. Add gymnasts in the dashboard first.</p>
                ) : (
                  <div className="space-y-3">
                    {gymnasts.map(gymnast => (
                      <div key={gymnast.id} className="border rounded-lg p-3 hover:bg-gray-50">
                        <div className="flex items-start space-x-3">
                          <Checkbox
                            id={`gymnast-${gymnast.id}`}
                            data-testid={`gymnast-checkbox-${gymnast.id}`}
                            checked={selectedGymnasts.includes(gymnast.id)}
                            onCheckedChange={() => handleGymnastToggle(gymnast.id)}
                            disabled={isSubmitted}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <Label htmlFor={`gymnast-${gymnast.id}`} className="cursor-pointer">
                              <div>
                                <div className="font-semibold">
                                  {gymnast.preferredFirstName || gymnast.firstName} {gymnast.preferredLastName || gymnast.lastName}
                                </div>
                                <div className="text-sm text-gray-600">
                                  BG: {gymnast.bgNumber} | DOB: {format(new Date(gymnast.dob), 'dd/MM/yyyy')}
                                </div>
                              </div>
                            </Label>
                            
                            {/* Category Selection */}
                            {selectedGymnasts.includes(gymnast.id) && !isSubmitted && (
                              <div className="mt-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <Label htmlFor={`category-${gymnast.id}`} className="text-xs">
                                    Category for this competition *
                                  </Label>
                                  {(() => {
                                    const suggested = suggestCategoryForGymnast(gymnast);
                                    return suggested && gymnastCategories[gymnast.id] === suggested ? (
                                      <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded" data-testid={`dob-suggested-${gymnast.id}`}>
                                        Auto-suggested
                                      </span>
                                    ) : null;
                                  })()}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Select 
                                    value={gymnastCategories[gymnast.id] || ''} 
                                    onValueChange={(value) => setGymnastCategories({...gymnastCategories, [gymnast.id]: value})}
                                  >
                                    <SelectTrigger id={`category-${gymnast.id}`} className="h-9 flex-1" data-testid={`category-select-${gymnast.id}`}>
                                      <SelectValue placeholder="Select category..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {categories.map(cat => {
                                        const cfg = competition?.categoriesConfig?.find(c => c.name === cat);
                                        const bands = competition?.categoryAgeBands?.[cat];
                                        const compYear = competition?.startsOn ? new Date(competition.startsOn).getFullYear() : new Date().getFullYear();
                                        const minA = bands?.minAge ?? cfg?.minAge ?? (cfg?.minBirthYear ? compYear - cfg.minBirthYear : null);
                                        const maxA = bands?.maxAge ?? cfg?.maxAge ?? (cfg?.maxBirthYear ? compYear - cfg.maxBirthYear : null);
                                        const ageLabel = minA && maxA ? ` (age ${minA}–${maxA})` : minA ? ` (age ${minA}+)` : maxA ? ` (under ${maxA + 1})` : '';
                                        return (
                                          <SelectItem key={cat} value={cat}>
                                            {cat}{ageLabel} — £{feeForCategory(cat).toFixed(2)}
                                          </SelectItem>
                                        );
                                      })}
                                    </SelectContent>
                                  </Select>
                                  {gymnastCategories[gymnast.id] && (
                                    <span
                                      className="shrink-0 inline-flex items-center justify-center px-2.5 h-8 rounded-md bg-gray-100 text-gray-800 text-sm font-semibold border"
                                      data-testid={`gymnast-fee-${gymnast.id}`}
                                    >
                                      £{feeForCategory(gymnastCategories[gymnast.id]).toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {selectedGymnasts.includes(gymnast.id) && isSubmitted && gymnastCategories[gymnast.id] && (
                              <div className="mt-2 text-sm text-gray-600 flex items-center gap-2">
                                <span>Category: <span className="font-medium">{gymnastCategories[gymnast.id]}</span></span>
                                <span className="inline-flex items-center px-2 h-6 rounded bg-gray-100 text-gray-700 text-xs font-semibold border">
                                  £{feeForCategory(gymnastCategories[gymnast.id]).toFixed(2)}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {isSubmitted && selectedGymnasts.includes(gymnast.id) && !entry?.withdrawnGymnasts?.some(w => w.gymnastId === gymnast.id) && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-gray-900 hover:bg-gray-100"
                              onClick={() => {
                                if (competition?.selfWithdrawEnabled === false) {
                                  setShowWithdrawBlockedDialog(true);
                                } else {
                                  handleOpenWithdrawDialog(gymnast);
                                }
                              }}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Withdraw
                            </Button>
                          )}
                          {isSubmitted && entry?.withdrawnGymnasts?.some(w => w.gymnastId === gymnast.id) && (
                            <span className="text-sm text-gray-900 font-medium px-3 py-1 bg-gray-100 rounded">
                              Withdrawn
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>


            {/* Petitioned Gymnasts — only shown when enabled for this competition */}
            {competition?.petitionedGymnastsEnabled === true && (
            <Card>
              <CardHeader>
                <CardTitle>Petitioned Gymnasts (Optional)</CardTitle>
                <CardDescription className="text-xs leading-relaxed">
                  <strong>For clubs who have put a petition in for their Gymnasts, please select them here.</strong> They will not be included in the total price. The competition organisers will review and accept/decline each petition. If accepted, they will be moved to the main gymnasts list automatically. We will also get in touch and tell you, and payment will need to be made as soon as possible.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {gymnasts.length === 0 ? (
                  <p className="text-gray-500">No gymnasts available for petition.</p>
                ) : (
                  <div className="space-y-3">
                    {gymnasts
                      .filter(g => !selectedGymnasts.includes(g.id))  // Only show gymnasts not in main list
                      .map(gymnast => {
                        const petition = petitionedGymnasts.find(p => p.gymnastId === gymnast.id);
                        const isPetitioned = !!petition;
                        const status = petition?.status || 'pending';
                        const isDisabled = isSubmitted || (isPetitioned && status !== 'pending');
                        
                        // Use preferred names for display
                        const displayFirstName = gymnast.preferredFirstName || gymnast.legalFirstName || gymnast.firstName || '';
                        const displayLastName = gymnast.preferredLastName || gymnast.legalLastName || gymnast.lastName || '';
                        
                        return (
                          <div key={gymnast.id} className={`border rounded-lg p-3 ${
                            status === 'accepted' ? 'bg-green-50 border-green-200' : 
                            status === 'declined' ? 'bg-gray-100 border-gray-300' : 
                            'bg-yellow-50 border-yellow-200'
                          }`}>
                            <div className="flex items-start space-x-3">
                              <Checkbox
                                id={`petition-${gymnast.id}`}
                                checked={isPetitioned}
                                onCheckedChange={() => handlePetitionedGymnastToggle(gymnast.id)}
                                disabled={isDisabled}
                                className="mt-1"
                              />
                              <div className="flex-1">
                                <Label htmlFor={`petition-${gymnast.id}`} className={`${isDisabled ? '' : 'cursor-pointer'}`}>
                                  <div className="flex items-center gap-2 mb-1">
                                    <div className="font-semibold">{displayFirstName} {displayLastName}</div>
                                    {status === 'accepted' && isPetitioned && (
                                      <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded font-semibold">
                                        ✓ ACCEPTED
                                      </span>
                                    )}
                                    {status === 'declined' && isPetitioned && (
                                      <span className="text-xs bg-gray-900 text-white px-2 py-0.5 rounded font-semibold">
                                        ✗ DECLINED
                                      </span>
                                    )}
                                    {status === 'pending' && isPetitioned && (
                                      <span className="text-xs bg-yellow-600 text-white px-2 py-0.5 rounded font-semibold">
                                        Pending petition outcome
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    BG: {gymnast.bgNumber} | DOB: {format(new Date(gymnast.dob), 'dd/MM/yyyy')}
                                  </div>
                                </Label>
                                
                                {/* Category Selection for Petitioned Gymnasts */}
                                {isPetitioned && status === 'pending' && (
                                  <div className="mt-3">
                                    <Label htmlFor={`petition-category-${gymnast.id}`} className="text-xs">Select Category *</Label>
                                    <Select
                                      value={gymnastCategories[gymnast.id] || ''}
                                      onValueChange={(value) => setGymnastCategories({...gymnastCategories, [gymnast.id]: value})}
                                    >
                                      <SelectTrigger id={`petition-category-${gymnast.id}`} className="mt-1">
                                        <SelectValue placeholder="Choose category..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {categories.map(cat => (
                                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </CardContent>
            </Card>
            )} {/* end petitionedGymnastsEnabled */}


            {/* Coaches */}
            <Card>
              <CardHeader>
                <CardTitle>Select Coaches</CardTitle>
                <CardDescription className="text-xs leading-relaxed space-y-1">
                  <p>ALL coaches, including those with a Level 1 Qualification, must be named on this entry form.</p>
                  {competition?.smsCoachesEnabled && (
                    <p className="text-blue-700 font-medium">
                      SMS alerts: as soon as a judge inputs a gymnast's score, toggled coaches will instantly receive a text message with that score — so you see your gymnast's result the moment it's entered. At least 1 must be enabled; maximum 3.
                    </p>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {coaches.length === 0 ? (
                  <p className="text-gray-500">No coaches added yet.</p>
                ) : (
                  <div className="space-y-3">
                    {coaches.map(coach => {
                      const isSelected = selectedCoaches.includes(coach.id);
                      const smsOn = !!coachSmsToggles[coach.id];
                      const phone = (coach.phone || '').trim();
                      const phoneOk = !!phone;
                      return (
                        <div
                          key={coach.id}
                          className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50"
                        >
                          <Checkbox
                            id={`coach-${coach.id}`}
                            data-testid={`coach-checkbox-${coach.id}`}
                            checked={isSelected}
                            onCheckedChange={() => handleCoachToggle(coach.id)}
                            disabled={isSubmitted}
                          />
                          <Label htmlFor={`coach-${coach.id}`} className="flex-1 cursor-pointer">
                            <div>
                              <div className="font-semibold">{coach.firstName} {coach.lastName}</div>
                              <div className="text-sm text-gray-600">
                                BG: {coach.bgNumber} | Qualification: {coach.qualification}
                                {phone && ` | Phone: ${phone}`}
                              </div>
                            </div>
                          </Label>

                          {/* SMS toggle — only visible when the competition has SMS enabled and this coach is selected */}
                          {competition?.smsCoachesEnabled && isSelected && (
                            <div
                              className="flex items-center gap-2 pl-3 border-l"
                              data-testid={`coach-sms-wrapper-${coach.id}`}
                            >
                              <Switch
                                id={`coach-sms-${coach.id}`}
                                checked={smsOn && phoneOk}
                                disabled={isSubmitted || !phoneOk}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    const currentCount = Object.values(coachSmsToggles).filter(Boolean).length;
                                    if (currentCount >= 3) {
                                      toast.error('Maximum 3 coaches can receive SMS alerts');
                                      return;
                                    }
                                  }
                                  setCoachSmsToggles(prev => ({ ...prev, [coach.id]: !!checked }));
                                }}
                                data-testid={`coach-sms-toggle-${coach.id}`}
                              />
                              <Label
                                htmlFor={`coach-sms-${coach.id}`}
                                className={`text-xs font-medium select-none ${phoneOk ? 'text-gray-700' : 'text-gray-400'}`}
                              >
                                {phoneOk ? 'SMS' : 'No phone on profile'}
                              </Label>
                              {/* Show count badge when this toggle is on */}
                              {smsOn && phoneOk && (() => {
                                const total = Object.values(coachSmsToggles).filter(Boolean).length;
                                return <span className="text-xs text-blue-600 font-bold">{total}/3</span>;
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <div className="flex items-start space-x-2 p-4 bg-gray-50 rounded-lg mt-4">
                      <Checkbox
                        id="coach-confirmation"
                        checked={coachConfirmation}
                        onCheckedChange={setCoachConfirmation}
                        disabled={isSubmitted}
                      />
                      <Label htmlFor="coach-confirmation" className="text-xs leading-relaxed cursor-pointer">
                        I confirm that the coaches entered above have a current British Gymnastics Enhanced Membership, and any Level 1's must have a current Competitive Level Membership.
                      </Label>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Judges — hidden when the competition has judges requirement turned off */}
            {competition?.judgesRequired !== false && (
            <Card>
              <CardHeader>
                <CardTitle>Select Judge(s)</CardTitle>
                <CardDescription className="text-xs leading-relaxed space-y-2">
                  <p className="font-semibold">All clubs need to volunteer a qualified WAG judge in order to enter this competition. Any entries without a suitable judge will not be accepted.</p>
                  {selectedGymnasts.length > 0 && (
                    <p className="text-sm font-medium text-black">
                      Based on {selectedGymnasts.length} gymnast{selectedGymnasts.length !== 1 ? 's' : ''}, you need to provide <strong>{requiredJudgesForCurrentEntry()} judge-day{requiredJudgesForCurrentEntry() !== 1 ? 's' : ''}</strong>.
                      {(competition?.competitionDays || 1) > 1 && (
                        <span className="text-xs text-gray-500 ml-1">
                          ({totalJudgeDays()} of {requiredJudgesForCurrentEntry()} covered)
                        </span>
                      )}
                      {competition?.judgeRequirementLadder?.length > 0 && <span className="text-xs text-gray-500 ml-1">(ladder applied)</span>}
                    </p>
                  )}
                  {competition?.missingJudgeFinePence > 0 && (
                    <p className="text-sm text-amber-700 font-medium">
                      Missing judge fine: £{(competition.missingJudgeFinePence / 100).toFixed(2)}
                    </p>
                  )}
                  <p>Any of the following category of judge can be used:</p>
                  <ol className="list-decimal ml-4 space-y-1">
                    <li>Any judge who has passed their online exam (for 2025-28 cycle)</li>
                    <li>Any qualified judge from last cycle</li>
                    <li>Any new judge who has attended ALL the South sessions, even if they have not yet passed their exam</li>
                  </ol>
                </CardDescription>
              </CardHeader>
              <CardContent>
                {judges.length === 0 ? (
                  <div className="space-y-4">
                    <p className="text-gray-500">No judges added yet.</p>
                    
                    {/* Judge Bypass Option - when no judges exist */}
                    {!isSubmitted && (
                      <div className="pt-4 border-t border-gray-200">
                        {bypassUsed ? (
                          <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <span className="text-sm text-green-800">
                              Judge requirement bypassed. You can proceed without selecting a judge.
                            </span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-start gap-2">
                            <span className="text-xs text-gray-500">
                              {competition?.judgeBypassMessage || "Don't have a judge available? Contact Scorz for a one-time bypass code."}
                            </span>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setShowBypassDialog(true)}
                              className="border-black text-black hover:bg-gray-100"
                              data-testid="judge-bypass-button-no-judges"
                            >
                              <Key className="w-4 h-4 mr-2" />
                              Enter Bypass Code
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {judges.map(judge => (
                      <div key={judge.id} className="border rounded-lg hover:bg-gray-50">
                        <div className="flex items-center space-x-3 p-3">
                          <Checkbox
                            id={`judge-${judge.id}`}
                            data-testid={`judge-checkbox-${judge.id}`}
                            checked={selectedJudges.includes(judge.id)}
                            onCheckedChange={() => handleJudgeToggle(judge.id)}
                            disabled={isSubmitted}
                          />
                          <Label htmlFor={`judge-${judge.id}`} className="flex-1 cursor-pointer">
                            <div>
                              <div className="font-semibold">{judge.firstName} {judge.lastName}</div>
                              <div className="text-sm text-gray-600">BG: {judge.bgNumber} | Level: {judge.level}</div>
                            </div>
                          </Label>
                        </div>
                        {/* Per-day assignment — shown whenever judge is selected on a multi-day competition */}
                        {selectedJudges.includes(judge.id) && (competition?.competitionDays || 1) > 1 && (
                          <div className="px-3 pb-3 pl-10 border-t border-gray-100 pt-2" data-testid={`judge-day-row-${judge.id}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-gray-700">Which day(s) will this judge attend?</span>
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                (judgeDayAssignments[judge.id]?.length || 0) > 0
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-500'
                              }`}>
                                Covers {judgeDayAssignments[judge.id]?.length || 0} judge-day{(judgeDayAssignments[judge.id]?.length || 0) !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {getCompDayLabels().map((label, i) => {
                                const dayNum = i + 1;
                                const isChecked = judgeDayAssignments[judge.id]?.includes(dayNum) ?? true;
                                return (
                                  <label key={dayNum} className={`flex items-center gap-1.5 text-xs cursor-pointer select-none px-2.5 py-1.5 rounded-md border transition-colors ${
                                    isChecked
                                      ? 'bg-blue-50 border-blue-300 text-blue-800 font-medium'
                                      : 'bg-gray-50 border-gray-200 text-gray-500'
                                  }`}>
                                    <Checkbox
                                      checked={isChecked}
                                      onCheckedChange={(v) => handleJudgeDayToggle(judge.id, dayNum, !!v)}
                                      disabled={isSubmitted}
                                      data-testid={`judge-day-${judge.id}-${dayNum}`}
                                    />
                                    <span>{label}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {selectedJudges.length > 0 && (
                      <div className="flex items-start space-x-2 p-4 bg-gray-50 rounded-lg mt-4">
                        <Checkbox
                          id="judge-confirmation"
                          checked={judgeConfirmation}
                          onCheckedChange={setJudgeConfirmation}
                          disabled={isSubmitted}
                        />
                        <Label htmlFor="judge-confirmation" className="text-xs leading-relaxed cursor-pointer">
                          I confirm that the judges I have put forward are aware that they have been selected for '{competition?.title}' on {competition?.displayDate}
                        </Label>
                      </div>
                    )}

                    {/* Fine acknowledgement — shown when short on judges but have at least one */}
                    {!isSubmitted && selectedJudges.length > 0 && totalJudgeDays() < requiredJudgesForCurrentEntry() && competition?.missingJudgeFinePence > 0 && (
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <Checkbox
                            id="judge-fine-ack"
                            data-testid="judge-fine-ack-checkbox"
                            checked={judgeFineAcknowledged}
                            onCheckedChange={setJudgeFineAcknowledged}
                          />
                          <Label htmlFor="judge-fine-ack" className="text-xs leading-relaxed cursor-pointer text-amber-900">
                            I am short by {requiredJudgesForCurrentEntry() - totalJudgeDays()} judge-day{requiredJudgesForCurrentEntry() - totalJudgeDays() !== 1 ? 's' : ''} and agree to pay the missing judge fine of{' '}
                            <strong>£{((competition.missingJudgeFinePence / 100) * (requiredJudgesForCurrentEntry() - totalJudgeDays())).toFixed(2)}</strong>{' '}
                            (£{(competition.missingJudgeFinePence / 100).toFixed(2)} per missing judge-day).
                          </Label>
                        </div>
                      </div>
                    )}
                    
                    {/* Judge Bypass Option */}
                    {!isSubmitted && selectedJudges.length === 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        {bypassUsed ? (
                          <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <span className="text-sm text-green-800">
                              Judge requirement bypassed. You can proceed without selecting a judge.
                            </span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-start gap-2">
                            <span className="text-xs text-gray-500">
                              {competition?.judgeBypassMessage || "Don't have a judge available? Contact Scorz for a one-time bypass code."}
                            </span>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setShowBypassDialog(true)}
                              className="border-black text-black hover:bg-gray-100"
                              data-testid="judge-bypass-button"
                            >
                              <Key className="w-4 h-4 mr-2" />
                              Enter Bypass Code
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Team Events */}
            {competition?.teamEventEnabled && (
            <Card data-testid="teams-card">
              <CardHeader>
                <CardTitle>Team Event {competition?.teamEntryFee > 0 ? `(£${(competition.teamEntryFee / 100).toFixed(2)} per team)` : ''}</CardTitle>
                <CardDescription>
                  Enter a team for this competition. Each team can have up to {competition?.teamSize || 6} gymnasts; the top {competition?.teamCountingScores || 4} apparatus scores count toward the team total. Gymnasts can be in a team without needing to compete individually.
                  {competition?.teamMaxPerClub > 0 && (
                    <> Max {competition.teamMaxPerClub} team{competition.teamMaxPerClub !== 1 ? 's' : ''} per club.</>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Existing teams */}
                {teams.length > 0 ? (
                  <div className="space-y-2">
                    {teams.map((team) => (
                      <div
                        key={team.id}
                        data-testid={`team-row-${team.id}`}
                        className={`flex items-start justify-between gap-3 p-3 border rounded-lg ${team.status === 'withdrawn' ? 'bg-gray-50 opacity-60' : 'bg-white'}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold flex items-center gap-2">
                            <span>{team.name}</span>
                            {team.discipline && <span className="text-xs px-2 py-0.5 rounded-full bg-black text-white">{team.discipline}</span>}
                            {team.status === 'withdrawn' && <span className="text-xs text-red-600">(withdrawn)</span>}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            {team.gymnastIds.length} gymnast{team.gymnastIds.length !== 1 ? 's' : ''}
                            {(team.feePence || 0) > 0 && ` · £${(team.feePence / 100).toFixed(2)}`}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {team.gymnastIds
                              .map(gid => gymnasts.find(g => g.id === gid))
                              .filter(Boolean)
                              .map(g => `${g.preferredFirstName || g.firstName} ${g.preferredLastName || g.legalLastName || g.lastName}`)
                              .join(', ')}
                          </div>
                        </div>
                        {!isSubmitted && team.status !== 'withdrawn' && (
                          <div className="flex flex-col gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`edit-team-${team.id}`}
                              onClick={() => openEditTeamForm(team)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-300 hover:bg-red-50"
                              data-testid={`delete-team-${team.id}`}
                              onClick={() => handleDeleteTeam(team.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No teams entered yet.</p>
                )}

                {/* Team form */}
                {!showTeamForm && !isSubmitted && (
                  <Button
                    variant="outline"
                    onClick={openCreateTeamForm}
                    disabled={(competition?.teamMaxPerClub > 0 && teams.filter(t => t.status !== 'withdrawn').length >= competition.teamMaxPerClub)}
                    className="border-black text-black hover:bg-gray-100"
                    data-testid="add-team-button"
                  >
                    + Add a team
                  </Button>
                )}
                {!showTeamForm && selectedGymnasts.length === 0 && (
                  <p className="text-xs text-gray-500">Select at least one gymnast above before creating a team.</p>
                )}

                {showTeamForm && (
                  <div className="border rounded-lg p-4 space-y-3 bg-gray-50" data-testid="team-form">
                    <div>
                      <Label className="text-sm font-semibold">Team name</Label>
                      <Input
                        data-testid="team-name-input"
                        value={teamName}
                        onChange={(e) => setTeamName(e.target.value)}
                        placeholder="e.g. Senior A"
                        className="mt-1"
                      />
                    </div>
                    {(() => {
                      // Team categories from categoriesConfig (new) OR competition.teamCategories (legacy)
                      const tCats = teamCategoryOptions.length > 0
                        ? teamCategoryOptions
                        : (Array.isArray(competition?.teamCategories) ? competition.teamCategories : []);
                      return tCats.length > 0 ? (
                        <div>
                          <Label className="text-sm font-semibold">Team category</Label>
                          <Select value={teamCategoryName || 'none'} onValueChange={handleTeamCategoryChange}>
                            <SelectTrigger data-testid="team-category-name-select" className="mt-1">
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">— None —</SelectItem>
                              {tCats.map(tc => (
                                <SelectItem key={tc.name} value={tc.name}>
                                  {tc.name}
                                  {tc.notes ? ` — ${tc.notes}` : ''}
                                  {tc.feeGbp ? ` — £${tc.feeGbp.toFixed(2)}` : tc.feePence ? ` — £${(tc.feePence / 100).toFixed(2)}` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : null;
                    })()}
                    {(competition?.sheetsDiscipline === 'BOTH') && (
                      <div>
                        <Label className="text-sm font-semibold">Discipline (optional)</Label>
                        <Select value={teamDiscipline || 'any'} onValueChange={(v) => setTeamDiscipline(v === 'any' ? '' : v)}>
                          <SelectTrigger data-testid="team-discipline-select" className="mt-1">
                            <SelectValue placeholder="Any" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Any</SelectItem>
                            <SelectItem value="WAG">WAG</SelectItem>
                            <SelectItem value="MAG">MAG</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div>
                      <Label className="text-sm font-semibold">
                        Gymnasts ({teamGymnastIds.length}/{competition?.teamSize || 6})
                      </Label>
                      <p className="text-xs text-gray-500 mb-1">Team members do not need to be individually competing — all your gymnasts are available.</p>
                      <div className="mt-1 space-y-1 max-h-64 overflow-y-auto border rounded p-2 bg-white">
                        {gymnasts.length === 0 ? (
                          <p className="text-xs text-gray-500 p-2">No gymnasts in your club profile yet.</p>
                        ) : (
                          gymnasts.map(g => (
                            <div key={g.id} className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded">
                              <Checkbox
                                id={`team-gym-${g.id}`}
                                checked={teamGymnastIds.includes(g.id)}
                                onCheckedChange={() => toggleTeamGymnast(g.id)}
                                data-testid={`team-gymnast-toggle-${g.id}`}
                              />
                              <Label htmlFor={`team-gym-${g.id}`} className="cursor-pointer text-sm flex-1">
                                {g.preferredFirstName || g.firstName} {g.preferredLastName || g.legalLastName || g.lastName}
                                {selectedGymnasts.includes(g.id) && gymnastCategories[g.id] && (
                                  <span className="text-xs text-gray-500 ml-2">({gymnastCategories[g.id]})</span>
                                )}
                                {!selectedGymnasts.includes(g.id) && (
                                  <span className="text-xs text-blue-500 ml-2">team only</span>
                                )}
                              </Label>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button
                        onClick={handleSaveTeam}
                        disabled={teamSaving}
                        className="bg-[#000000] hover:bg-[#333333]"
                        data-testid="save-team-button"
                      >
                        {teamSaving ? 'Saving…' : (editingTeamId ? 'Update Team' : 'Create Team')}
                      </Button>
                      <Button variant="outline" onClick={resetTeamForm} disabled={teamSaving}>Cancel</Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            )}
          </div>

          {/* Summary & Actions */}
          <div className="space-y-6">
            {/* Total */}
            <Card className="border-[#000000]">
              <CardHeader>
                <CardTitle>Entry Total</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {/* Per-category breakdown when category fees vary, otherwise simple line */}
                  {(() => {
                    const cf = competition.categoryFees || {};
                    const hasVariedFees = Object.keys(cf).length > 0;
                    if (!hasVariedFees) {
                      return (
                        <div className="flex justify-between">
                          <span>Gymnasts ({selectedGymnasts.length})</span>
                          <span>£{(selectedGymnasts.length * (parseFloat(competition.entryFee) || 0)).toFixed(2)}</span>
                        </div>
                      );
                    }
                    // Group selected gymnasts by their chosen category
                    const groups = {};
                    selectedGymnasts.forEach(gid => {
                      const cat = gymnastCategories[gid] || '— No category —';
                      if (!groups[cat]) groups[cat] = 0;
                      groups[cat] += 1;
                    });
                    const entries = Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
                    return (
                      <>
                        {entries.map(([cat, count]) => {
                          const fee = feeForCategory(cat === '— No category —' ? null : cat);
                          return (
                            <div key={cat} className="flex justify-between text-sm">
                              <span className="text-gray-700">{cat} ({count} × £{fee.toFixed(2)})</span>
                              <span>£{(count * fee).toFixed(2)}</span>
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                  <div className="flex justify-between text-gray-600 text-sm">
                    <span>Coaches ({selectedCoaches.length})</span>
                    <span></span>
                  </div>
                  {competition?.judgesRequired !== false && (
                    <div className="flex justify-between text-gray-600 text-sm">
                      <span>Judges ({selectedJudges.length})</span>
                      <span></span>
                    </div>
                  )}
                  {competition?.teamEventEnabled && teams.filter(t => t.status !== 'withdrawn').length > 0 && (
                    <div className="flex justify-between text-sm" data-testid="teams-total-line">
                      <span className="text-gray-700">
                        Teams ({teams.filter(t => t.status !== 'withdrawn').length})
                      </span>
                      <span>
                        £{(teams.filter(t => t.status !== 'withdrawn').reduce((s, t) => s + ((t.feePence || 0) / 100), 0)).toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between text-xl font-bold">
                      <span>{entry?.submittedAt ? 'Total Paid' : 'Total'}</span>
                      <span data-testid="total-amount" className="text-[#000000]">
                        £{entry?.submittedAt && entry?.totalPaid !== undefined 
                          ? entry.totalPaid.toFixed(2) 
                          : calculateTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Upload */}
            <Card>
              <CardHeader>
                <CardTitle>Payment</CardTitle>
                <CardDescription className="space-y-2">
                  {competition?.bankAccountName ? (
                    <div className="text-xs leading-relaxed">
                      <p className="font-semibold mb-1">Bank Details:</p>
                      <p>Name on Account: {competition.bankAccountName}</p>
                      {competition.bankName && <p>Bank: {competition.bankName}</p>}
                      <p>Account Number: {competition.bankAccountNumber}</p>
                      <p>Sort Code: {competition.bankSortCode}</p>
                    </div>
                  ) : (
                    <p className="text-xs leading-relaxed text-yellow-600">
                      Bank details not yet provided. Contact the organiser.
                    </p>
                  )}
                  {competition?.referenceTemplate ? (
                    <p className="text-xs font-bold text-[#000000]">
                      PAYMENT REFERENCE: {competition.referenceTemplate
                        .replace('{CLUB_NAME}', (club?.name || 'CLUB').toUpperCase())
                        .replace('{CLUB_CODE}', (club?.clubCode || '').toUpperCase())
                        .replace('{COMP_DATE}', competition?.startsOn ? format(new Date(competition.startsOn), 'ddMMM').toUpperCase() : '')
                        .replace('{COMP_TITLE}', (competition?.title || '').substring(0, 20).toUpperCase())
                      }
                    </p>
                  ) : (
                    <p className="text-xs font-bold text-[#000000]">
                      PLEASE USE {formatPaymentReference(competition?.startsOn)} AS REFERENCE
                    </p>
                  )}
                  {competition?.paymentProofRequired !== false && (
                    <p className="text-xs">Upload BACS payment screenshot (JPG, PNG, or PDF)</p>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {competition?.paymentProofRequired === false ? (
                  /* No upload required — show custom payment message if set */
                  <div data-testid="payment-message-block" className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                    <svg className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span>
                      {competition?.paymentMessage?.trim()
                        ? competition.paymentMessage
                        : 'No payment screenshot required for this competition. Please arrange payment directly with the organiser.'}
                    </span>
                  </div>
                ) : entry?.paymentProofUrl ? (
                  <div data-testid="payment-proof-uploaded" className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="w-5 h-5" />
                    <span>Payment proof uploaded</span>
                  </div>
                ) : (
                  <>
                    <input
                      type="file"
                      data-testid="payment-proof-input"
                      accept="image/jpeg,image/png,application/pdf"
                      onChange={handleFileChange}
                      disabled={isSubmitted}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-lg file:border-0
                        file:text-sm file:font-semibold
                        file:bg-[#000000] file:text-white
                        hover:file:bg-[#333333]
                        file:cursor-pointer"
                    />
                    {paymentFile && (
                      <Button
                        data-testid="upload-payment-button"
                        onClick={handleUploadPayment}
                        disabled={uploading || isSubmitted}
                        className="w-full bg-[#000000] hover:bg-[#333333]"
                      >
                        {uploading ? 'Uploading...' : 'Upload Payment Proof'}
                      </Button>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="space-y-3">
              <Button
                data-testid="save-entry-button"
                onClick={handleSave}
                disabled={isSubmitted}
                variant="outline"
                className="w-full border-[#000000] text-[#000000] hover:bg-[#f5f5f5]"
              >
                Save Entry
              </Button>
              <Button
                data-testid="submit-entry-button"
                onClick={handlePreSubmit}
                disabled={
                  submitting || 
                  isSubmitted || 
                  (competition?.gymnastsRequired !== false && selectedGymnasts.length === 0) ||
                  (competition?.coachesRequired !== false && selectedCoaches.length < (Number.isFinite(competition?.minCoaches) ? competition.minCoaches : 1)) ||
                  (competition?.judgesRequired !== false && totalJudgeDays() < requiredJudgesForCurrentEntry() && !bypassUsed && !judgeFineAcknowledged) ||
                  (selectedJudges.length > 0 && !judgeConfirmation) ||
                  (selectedCoaches.length > 0 && !coachConfirmation) ||
                  (competition?.paymentProofRequired !== false && !entry?.paymentProofUrl)
                }
                className="w-full bg-[#000000] hover:bg-[#333333]"
              >
                {submitting ? 'Submitting...' : 'Submit Entry'}
              </Button>
              {isSubmitted && (
                <p className="text-sm text-gray-600 text-center">
                  Entry submitted on {format(new Date(entry.submittedAt), 'dd/MM/yyyy HH:mm')}
                </p>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Withdraw Gymnast Dialog */}
      <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">
              Withdraw {gymnastToWithdraw?.preferredFirstName || gymnastToWithdraw?.firstName}
            </DialogTitle>
          </DialogHeader>

          {!refundCaseId ? (
            <div className="space-y-4 py-4">
              {/* Top Alert */}
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4">
                <p className="font-bold text-amber-900">
                  Withdrawing {gymnastToWithdraw?.preferredFirstName || gymnastToWithdraw?.firstName} does not automatically grant a refund. Refunds are only considered after evidence is emailed.
                </p>
              </div>

              {/* Refund Requirements */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                <p className="font-semibold">Refund requirements (always):</p>
                <ul className="list-disc ml-5 space-y-1 text-gray-700">
                  <li>Email supporting evidence (e.g., medical/physio note or relevant photo) to <strong>support@scorz.co.uk</strong></li>
                  <li>You must email this evidence within <strong>7 days</strong> of withdrawing {gymnastToWithdraw?.preferredFirstName || gymnastToWithdraw?.firstName}</li>
                </ul>
              </div>

              {/* How to Claim Refund */}
              <div className="space-y-2 text-sm">
                <p className="font-semibold">How to claim a refund for {gymnastToWithdraw?.preferredFirstName || gymnastToWithdraw?.firstName}:</p>
                <ol className="list-decimal ml-5 space-y-1 text-gray-700">
                  <li>Choose a reason for withdrawing {gymnastToWithdraw?.preferredFirstName || gymnastToWithdraw?.firstName}</li>
                  <li>Click <strong>"Withdraw & start refund claim"</strong></li>
                  <li>A Refund Case ID will be created and emailed to you</li>
                  <li>Email evidence to support@scorz.co.uk with the case ID in subject</li>
                  <li>We'll review and email a decision (usually 5-7 working days)</li>
                </ol>
                <p className="text-amber-700 font-medium mt-2">⏰ Deadline: Evidence must be emailed within 7 days of this withdrawal</p>
              </div>

              {/* Fields */}
              <div>
                <Label htmlFor="withdrawReason">Reason for withdrawing {gymnastToWithdraw?.preferredFirstName || gymnastToWithdraw?.firstName} *</Label>
                <Select value={withdrawReason} onValueChange={setWithdrawReason}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Injury">Injury</SelectItem>
                    <SelectItem value="Illness">Illness</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="withdrawNotes">
                  {withdrawReason === 'Other' ? 'Please specify reason *' : 'Notes (optional)'}
                </Label>
                <Textarea
                  id="withdrawNotes"
                  placeholder={withdrawReason === 'Other' ? 'Enter reason for withdrawal...' : 'Add any additional notes...'}
                  value={withdrawNotes}
                  onChange={(e) => setWithdrawNotes(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Confirmation Checkbox */}
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="refundConfirm"
                  checked={refundConfirmation}
                  onCheckedChange={setRefundConfirmation}
                />
                <Label htmlFor="refundConfirm" className="text-sm cursor-pointer">
                  I understand refunds are not automatic and require emailed evidence to support@scorz.co.uk within 7 days
                </Label>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-2 pt-2">
                <Button
                  onClick={() => handleWithdrawGymnast(true)}
                  disabled={!withdrawReason || !refundConfirmation || (withdrawReason === 'Other' && !withdrawNotes.trim())}
                  className="w-full bg-[#000000] hover:bg-[#333333]"
                >
                  Withdraw {gymnastToWithdraw?.preferredFirstName || gymnastToWithdraw?.firstName} & start refund claim
                </Button>
                <Button
                  onClick={() => handleWithdrawGymnast(false)}
                  disabled={!withdrawReason || (withdrawReason === 'Other' && !withdrawNotes.trim())}
                  variant="outline"
                  className="w-full"
                >
                  Withdraw {gymnastToWithdraw?.preferredFirstName || gymnastToWithdraw?.firstName} only (no refund)
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShowWithdrawDialog(false)}
                  className="w-full"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            /* Success View - Refund Case Created */
            <div className="space-y-4 py-4">
              <div className="bg-green-50 border-l-4 border-green-500 p-4">
                <p className="font-bold text-green-900">✓ Withdrawal Completed</p>
                <p className="text-sm text-green-800 mt-1">
                  Refund request #{refundCaseId} has been created for {gymnastToWithdraw?.preferredFirstName || gymnastToWithdraw?.firstName}
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="font-semibold text-amber-900 mb-2">Next Steps:</p>
                <ol className="list-decimal ml-5 space-y-2 text-sm text-amber-800">
                  <li>Email supporting evidence to <strong>support@scorz.co.uk</strong></li>
                  <li>Use the subject line with your case ID (see button below)</li>
                  <li>Submit within 7 days to be eligible for refund</li>
                </ol>
              </div>

              <a
                href={`mailto:support@scorz.co.uk?subject=Refund%20Evidence%20%E2%80%93%20${refundCaseId}%20%E2%80%93%20${user?.club?.name || 'Club'}%20%E2%80%93%20${gymnastToWithdraw?.preferredFirstName || gymnastToWithdraw?.firstName}%20${gymnastToWithdraw?.preferredLastName || gymnastToWithdraw?.lastName}`}
                className="block"
              >
                <Button className="w-full bg-[#000000] hover:bg-[#333333]">
                  Email evidence for {gymnastToWithdraw?.preferredFirstName || gymnastToWithdraw?.firstName} →
                </Button>
              </a>

              <Button
                variant="outline"
                onClick={() => setShowWithdrawDialog(false)}
                className="w-full"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Judge Bypass Code Dialog */}
      <Dialog open={showBypassDialog} onOpenChange={setShowBypassDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enter Bypass Code</DialogTitle>
            <DialogDescription>
              Enter the one-time bypass code provided by Scorz to proceed without a judge.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="bypass-code">Bypass Code</Label>
              <Input
                id="bypass-code"
                data-testid="bypass-code-input"
                placeholder="Enter 6-character code"
                value={bypassCode}
                onChange={(e) => setBypassCode(e.target.value.toUpperCase())}
                maxLength={6}
                className="text-center text-2xl font-mono tracking-widest uppercase"
                disabled={bypassLoading}
              />
            </div>
            <p className="text-xs text-gray-500">
              This code can only be used once. If you've already used a code and need another bypass, contact Scorz for a new code.
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowBypassDialog(false);
                setBypassCode('');
              }}
              disabled={bypassLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBypassSubmit}
              disabled={bypassLoading || bypassCode.length < 6}
              className="bg-black hover:bg-gray-800"
              data-testid="bypass-submit-button"
            >
              {bypassLoading ? 'Validating...' : 'Submit Code'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Withdraw Blocked Dialog (selfWithdrawEnabled=false) */}
      <Dialog open={showWithdrawBlockedDialog} onOpenChange={setShowWithdrawBlockedDialog}>
        <DialogContent className="sm:max-w-md" data-testid="withdraw-blocked-dialog">
          <DialogHeader>
            <DialogTitle>Withdrawal not available online</DialogTitle>
            <DialogDescription>
              {competition?.withdrawContactMessage?.trim()
                ? competition.withdrawContactMessage
                : 'Self-withdrawal is disabled for this competition. Please contact the organiser to withdraw a gymnast.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setShowWithdrawBlockedDialog(false)} data-testid="withdraw-blocked-close">
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pre-submit Team Prompt Dialog */}      <Dialog open={showTeamPrompt} onOpenChange={setShowTeamPrompt}>
        <DialogContent className="sm:max-w-md" data-testid="team-prompt-dialog">
          <DialogHeader>
            <DialogTitle>Add a Team Entry?</DialogTitle>
            <DialogDescription>
              This competition has team events. Would you like to add a team before submitting your entry?
              {teamCategoryOptions.length > 0 && (
                <span className="block mt-1 text-xs font-medium">
                  Available: {teamCategoryOptions.map(c => c.name).join(', ')}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              data-testid="team-prompt-skip"
              onClick={() => {
                setShowTeamPrompt(false);
                handleSubmit();
              }}
            >
              Submit without team
            </Button>
            <Button
              data-testid="team-prompt-add"
              className="bg-black text-white hover:bg-gray-800"
              onClick={() => {
                setShowTeamPrompt(false);
                openCreateTeamForm();
                // Scroll to team section
                setTimeout(() => {
                  document.querySelector('[data-testid="teams-card"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 100);
              }}
            >
              Yes, add a team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}